import React, { useState, useEffect } from 'react';
import { NavHashLink } from 'react-router-hash-link';

interface NavItem {
    label: string;
    id: string;
    icon: string;
}

interface ProjectNavProps {
    sections: string[];
}

const allNavItems: NavItem[] = [
    { label: 'Overview', id: 'overview', icon: 'FileText' },
    { label: 'Media', id: 'gallery', icon: 'Image' },
    { label: 'Features', id: 'features', icon: 'Zap' },
    { label: 'Timeline', id: 'timeline', icon: 'Clock' },
    { label: 'Results', id: 'results', icon: 'TrendingUp' },
    { label: 'Challenges', id: 'challenges', icon: 'AlertTriangle' },
];

const ProjectNav: React.FC<ProjectNavProps> = ({ sections }) => {
    const [isSticky, setIsSticky] = useState(false);
    const [activeSection, setActiveSection] = useState('');

    // Only show nav items for sections that actually exist on the page
    const navItems = allNavItems.filter(item => sections.includes(item.id));

    useEffect(() => {
        if (navItems.length > 0 && !activeSection) {
            setActiveSection(navItems[0].id);
        }
    }, [navItems]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            setIsSticky(scrollY > 150);

            for (const item of navItems) {
                const element = document.getElementById(item.id);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= 150 && rect.bottom >= 150) {
                        setActiveSection(item.id);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [navItems]);

    if (navItems.length === 0) return null;

    return (
        <nav
            className={`z-50 border-b border-[#0C2C39]/10 transition-all duration-300 ${isSticky
                ? 'fixed top-[114px] left-0 right-0 bg-white/80 backdrop-blur-md shadow-sm'
                : 'relative bg-white/40'
                }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <ul className="flex items-center justify-center sm:justify-start space-x-8 overflow-x-auto no-scrollbar py-4">
                    {navItems.map((item) => (
                        <li key={item.id} className="flex-shrink-0">
                            <NavHashLink
                                smooth
                                to={`#${item.id}`}
                                className={`text-[13px] font-black uppercase tracking-widest transition-all duration-300 relative pb-1 ${activeSection === item.id
                                    ? 'text-[#0C2C39]'
                                    : 'text-[#0C2C39]/40 hover:text-[#0C2C39]'
                                    }`}
                            >
                                {item.label}
                                {activeSection === item.id && (
                                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#0C2C39]"></span>
                                )}
                            </NavHashLink>
                        </li>
                    ))}
                </ul>
            </div>
        </nav>
    );
};

export default ProjectNav;
